// Intranet-side AI assistant wiring for the shared AppAssistant component.
//
// The shared AppAssistant (workspace/packages/shared/components/AppAssistant.js)
// calls these via `el.call('...')`:
//
//   - aiStream(messages, attachment, onChunk, onDone)
//       Streams the AI response to the governance ai-chat edge function
//       and burns credits via the shared credit-meter.
//
//   - aiPerformAction(action, s)
//       Dispatches a confirmed action proposed by the assistant
//       (create_ticket, create_event, create_note, create_kb,
//        update_ticket, move_ticket).
//
//   - aiActionStyles()
//       Returns the icon/color/label map used for proposed-action cards.
//
//   - aiSuggestions()
//       Returns the starter prompts shown in the Welcome view.
//
//   - aiWelcomeText()
//       Returns the Welcome copy shown above the suggestions.
//
//   - fetchPendingApprovals()  — TASK#4360
//       Returns tickets in state='awaiting_approval' for the governance
//       workspace. Server-side, the ai-chat edge function injects the same
//       data into the AI prompt; this client helper exists so non-AI
//       surfaces (or future inline cards) can surface the count/list with
//       the same query and avoid drift.
//
// Canvas registers nothing; the toggle button self-hides when
// `context.functions.aiStream` is absent.
//
//   - subscribeTicketRealtime(root)
//       Opens a Supabase realtime channel on public.tickets. On state
//       transitions (→done, blocked→in_progress, →awaiting_approval) for
//       tickets whose metadata.simona_user_id matches the signed-in user,
//       injects a system message into root.assistantMessages so Simona
//       auto-surfaces the update in the thread.
//
//   - unsubscribeTicketRealtime()
//       Removes the realtime channel opened by subscribeTicketRealtime.

// migrated 2026-04-27 — getSupabase + sb.channel/removeChannel replaced by
// the SDK realtime API (`workspaceProject.realtime.tickets` subscribe).
// SUPABASE_URL/ANON_KEY remain as exports for the AI edge fn fetch
// fallback at the bottom of this file (kept until the wrapper exposes a
// dedicated /workspace/ai-fallback route).
import {
  SUPABASE_URL,
  SUPABASE_ANON_KEY
} from '../../shared/functions/supabaseClient.js'
import { getSDK } from '../../shared/functions/sdk.js'

// Credit metering for ai-chat now happens server-side inside the
// `ai-chat` Supabase edge function — server knows the actual token
// count from the AI provider response, so client-side estimation is
// inherently inaccurate AND trivially manipulable from the browser.
// See server/packages/credit-meter for the canonical burn primitive
// used by edge functions.

const AI_CHAT_URL = `${SUPABASE_URL}/functions/v1/ai-chat`

// Resolve a fresh bearer session. Returns { token, session } so callers
// can reuse the session for follow-up reads (e.g. workspace_org_id)
// without hitting `getSession()` twice.
//
// SDK's TokenManager handles refresh internally — `getSession()` returns
// the live access token (or null if signed-out). Falls back to anon for
// the edge function path that still accepts an unauthenticated call.
// Async because the SDK proxy makes getSession() return a Promise.
async function _resolveBearer () {
  const anon = { token: SUPABASE_ANON_KEY, session: null }
  try {
    const session = await getSDK().getSession()
    if (!session?.access_token) return anon
    return { token: session.access_token, session }
  } catch (err) {
    console.error('[aiAssistant] getSession failed:', err)
    return anon
  }
}

export const aiStream = async function aiStream (messages, attachment, onChunk, onDone) {
  try {
    const { token } = await _resolveBearer()
    const doFetch = (bearerToken) => fetch(AI_CHAT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${bearerToken}`
      },
      body: JSON.stringify({ messages, attachedCard: attachment || null })
    })

    let res = await doFetch(token)
    if (res.status === 401 && token !== SUPABASE_ANON_KEY) {
      res = await doFetch(SUPABASE_ANON_KEY)
    }
    if (!res.ok || !res.body) {
      const err = await res.text()
      onDone('Connection error: ' + err)
      return
    }
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let full = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''
      // Batch chunks from one network read into a single onChunk call so
      // a multi-line SSE frame doesn't cascade into multiple root-state
      // updates (which re-run every reactive factory in the drawer).
      let batched = false
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const json = line.slice(6).trim()
        if (json === '[DONE]') continue
        try {
          const parsed = JSON.parse(json)
          if (parsed.text) {
            full += parsed.text
            batched = true
          }
        } catch { /* skip */ }
      }
      if (batched) onChunk(full)
    }
    onDone(full || 'Sorry, I could not generate a response.')
  } catch (e) {
    console.error('[aiAssistant] AI stream failed:', e)
    onDone('Connection error — please try again.')
  }
}

export const aiActionStyles = function aiActionStyles () {
  return {
    create_ticket: { icon: 'check', color: 'orange', label: 'Create Ticket' },
    create_event: { icon: 'date outline', color: 'blue', label: 'Create Event' },
    create_note: { icon: 'document', color: 'green', label: 'Create Note' },
    create_kb: { icon: 'document', color: 'cyan', label: 'Create KB Article' },
    update_ticket: { icon: 'edit', color: 'yellow', label: 'Update Ticket' },
    move_ticket: { icon: 'arrowRight', color: 'purple', label: 'Move Ticket' }
  }
}

export const aiSuggestions = function aiSuggestions () {
  return [
    { text: 'Who is on the team?' },
    { text: 'What events are coming up?' },
    { text: 'Show me company documents' },
    { text: 'What tickets are open?' },
    { text: 'What is pending approval?' }
  ]
}

// fetchPendingApprovals — TASK#4360
// Note: the previous implementation hard-coded the governance workspace_id
// (69e5911201b0ef47b675463f) into the REST query so unauthenticated/anon
// callers could still pull governance approvals. After migrating to the
// SDK, scoping comes from the caller's JWT (active_workspace_id claim) —
// which is correct multi-tenant behavior. Anon callers can no longer
// query approvals; they need to authenticate first.
//
// Returns the list of tickets currently in state='awaiting_approval' for the
// governance workspace. These tickets are blocked from agents until a human
// approves them via the /tickets/general page (where the Approve action
// transitions state → in_progress).
//
// The server-side `ai-chat` edge function injects the same query result into
// the AI's prompt context under "PENDING APPROVALS" so Simona can surface
// them naturally when asked. This client helper exists for parity — non-AI
// surfaces (counters, badges, future inline cards) can call into it via
// `el.call('fetchPendingApprovals')` and stay in sync with the server.
//
// Returns: Promise<Array<{
//   id, number, title, priority, labels, assignee_email, reporter_email,
//   updated_at, created_at, ageMs, needsHumanApproval
// }>> — sorted newest-first. Empty array on error or no rows.
export const fetchPendingApprovals = async function fetchPendingApprovals () {
  try {
    // migrated 2026-04-27 to SDK route workspaceProject.tickets list
    // The wrapper applies the workspace scope from the JWT, so the
    // _GOVERNANCE_WORKSPACE_ID constant is no longer threaded — callers
    // already running under the governance workspace JWT will see only
    // governance rows. Other workspaces would see THEIR awaiting_approval
    // tickets, which is the correct multi-tenant behavior.
    const result = await getSDK().execute('workspaceProject.tickets', 'list', {
      filter: {
        state: 'awaiting_approval',
        deleted: false
      },
      options: {
        columns:
          'id,number,title,priority,labels,assignee_email,reporter_email,updated_at,created_at'
      },
      order: { column: 'updated_at', ascending: false },
      limit: 50
    })
    const rows = Array.isArray(result?.data)
      ? result.data
      : (result?.data || result || [])
    if (!Array.isArray(rows)) return []
    const now = Date.now()
    return rows.map((t) => {
      const labels = Array.isArray(t.labels) ? t.labels : []
      const ts = t.updated_at || t.created_at
      const ageMs = ts ? now - new Date(ts).getTime() : null
      return {
        ...t,
        labels,
        ageMs: Number.isFinite(ageMs) && ageMs >= 0 ? ageMs : null,
        needsHumanApproval: labels.includes('human-approval-needed')
      }
    })
  } catch (err) {
    console.error('[aiAssistant] fetchPendingApprovals error:', err)
    return []
  }
}

export const aiWelcomeText = function aiWelcomeText () {
  return 'Ask me about documents, team, calendar, or tickets.'
}

// Action → [registered function name, payload builder]. `me` is the
// current user's email. Each builder returns the payload to pass into
// the named function via `el.call`.
const _ACTION_DISPATCH = {
  create_ticket: (a, me) => ['saveTicketToSupabase', {
    title: a.title || 'New ticket',
    body: a.body || '',
    columnKey: 'backlog',
    priority: a.priority || 'medium',
    assigneeEmail: a.assignee || me,
    reporterEmail: me
  }],
  create_event: (a, me) => ['saveEventToSupabase', {
    title: a.title || 'New event',
    date: a.date || new Date().toISOString().slice(0, 10),
    startTime: a.startTime || '09:00',
    endTime: a.endTime || '10:00',
    type: 'meeting',
    creatorEmail: me
  }],
  create_note: (a) => ['createNote', {
    title: a.title || 'New note',
    content: a.content || a.body || '',
    source: 'ai'
  }],
  create_kb: (a) => ['saveKbArticle', {
    title: a.title || 'New article',
    category: a.category || 'General',
    content: a.content || a.body || '',
    summary: (a.content || a.body || '').slice(0, 100)
  }],
  move_ticket: (a) => ['moveTicket', {
    ticketId: a.number,
    columnKey: a.column_key,
    position: 0
  }]
}

export const aiPerformAction = function aiPerformAction (a, s) {
  if (!a) return
  const root = (s && s.root) || this?.state?.root
  const me = root?.currentUserEmail
  const type = a.type

  // `update_ticket` writes directly through the SDK rather than through a
  // registered mutator, so it stays a special case outside the table.
  if (type === 'update_ticket') {
    const row = { updated_at: new Date().toISOString() }
    if (a.body) row.body = a.body
    if (a.title) row.title = a.title
    if (a.column_key) row.column_key = a.column_key
    if (a.priority) row.priority = a.priority
    // migrated 2026-04-27 to SDK route workspaceProject.tickets update
    getSDK().execute('workspaceProject.tickets', 'update', {
      number: a.number,
      payload: row
    }).catch((err) => console.error('[aiAssistant] update_ticket failed:', err))
    return
  }

  const builder = _ACTION_DISPATCH[type]
  if (!builder) return
  const [fnName, payload] = builder(a, me)
  this.call(fnName, payload)
}

// ─── Realtime ticket subscription ──────────────────────────────────────────
//
// Watches public.tickets via Supabase realtime. When a Simona-originated
// ticket (metadata.simona_user_id === current user) transitions to a
// noteworthy state, injects a system notification card into the assistant
// thread so Simona surfaces it without being invoked manually.
//
// Design invariants:
//   - One channel per session; calling subscribeTicketRealtime again
//     while already subscribed is a no-op (returns existing channel).
//   - User identity comes from root.currentUserId (preferred) with
//     root.currentUserEmail as fallback — matches how simona.md tags tickets.
//   - System messages use role:'system' so AppAssistantMessage renders them
//     with the 'AI' role label and the standard assistant bubble style.
//   - unsubscribeTicketRealtime() removes the channel and clears the ref so
//     the AppAssistant onRemove lifecycle can clean up without leaking sockets.

// Noteworthy state transitions: oldState → newState label shown to user.
const _TRANSITION_LABEL = {
  done: 'shipped',
  awaiting_approval: 'awaiting your approval',
  in_progress: 'started'
}

// Build the human-readable notification text for a ticket state change.
const _notifyText = (ticket, newState) => {
  const label = _TRANSITION_LABEL[newState] || newState
  const title = ticket.title ? `"${ticket.title}"` : `#${ticket.id}`
  const sha = ticket.metadata?.commitSha
    ? ` (commit \`${ticket.metadata.commitSha.slice(0, 7)}\`)`
    : ''
  // Intranet ticket deep-link — relative path works for all origins.
  const href = `/tickets/${ticket.id}`
  const link = `[View ticket →](${href})`
  return `Your ticket ${title} just **${label}**${sha}. ${link}`
}

// Module-level subscription handle so subscribe/unsubscribe are stateless
// from the component's perspective — it just calls el.call('subscribeTicketRealtime', root).
//
// migrated 2026-04-27 to SDK realtime — `workspaceProject.realtime.tickets`
// dispatches through the SDK's transport-agnostic provider. Today's wire
// is Supabase realtime via supabaseRealtimeProvider in bootShell; backend
// can swap to native socket.io without touching this file.
let _ticketUnsub = null
let _ticketChannelUserId = null   // identity the channel was opened for

export const subscribeTicketRealtime = function subscribeTicketRealtime (root) {
  const userId = root?.currentUserId || root?.currentUserEmail || null

  // If already subscribed for the same user, skip.
  if (_ticketUnsub && _ticketChannelUserId === userId) return _ticketUnsub

  // If subscribed for a DIFFERENT user (workspace switch / sign-out + re-in),
  // tear down and re-subscribe for the new identity.
  if (_ticketUnsub && _ticketChannelUserId !== userId) {
    unsubscribeTicketRealtime()
  }

  // No user yet — defer; subscribeTicketRealtime will be called again once
  // root.currentUserId populates (AppAssistant.Panel.Thread.onUpdate retries).
  if (!userId) return null

  _ticketChannelUserId = userId

  let _resolved = null
  let _cancelled = false

  Promise.resolve(getSDK().getService?.('workspaceProject'))
    .then((wp) => {
      if (_cancelled || !wp?.realtime?.subscribeTickets) return
      _resolved = wp.realtime.subscribeTickets({}, (event, payload) => {
        if (event !== 'UPDATE') return
        const ticket = payload?.new
        const oldTicket = payload?.old
        if (!ticket) return

        // Filter: only tickets Simona tagged for this user.
        const meta = ticket.metadata || {}
        const ticketUserId = meta.simona_user_id
        if (!ticketUserId || ticketUserId !== userId) return

        const newState = ticket.state
        const oldState = oldTicket?.state

        // Ignore transitions that aren't noteworthy.
        if (newState === oldState) return
        if (!_TRANSITION_LABEL[newState]) return
        // 'in_progress' is only interesting when coming from 'blocked'
        // (downstream dep shipped). Ignore fresh pick-up from backlog.
        if (newState === 'in_progress' && oldState !== 'blocked') return

        // Inject a system notification into the assistant thread. The
        // `root.update` call re-runs every reactive factory in the drawer —
        // structurally safe because we append, never splice.
        const notifMsg = {
          role: 'system',
          content: _notifyText(ticket, newState),
          _ticketNotif: true,
          _ticketId: ticket.id,
          _ticketState: newState
        }
        const current = root?.assistantMessages || []
        if (root?.update) {
          root.update({
            assistantMessages: [...current, notifMsg],
            assistantOpen: true
          })
        }
      })
    })
    .catch((err) => {
      console.warn('[aiAssistant] subscribeTicketRealtime failed:', err?.message || err)
    })

  // Stable wrapper around the (possibly still-resolving) subscription.
  _ticketUnsub = () => {
    _cancelled = true
    if (typeof _resolved === 'function') {
      try { _resolved() } catch (_) {}
    }
  }
  return _ticketUnsub
}

export const unsubscribeTicketRealtime = function unsubscribeTicketRealtime () {
  if (!_ticketUnsub) return
  try { _ticketUnsub() } catch (_) {}
  _ticketUnsub = null
  _ticketChannelUserId = null
}
